import React, { useCallback, useContext, useState } from 'react';
import ServiceContext from './ServiceContext';
import serviceModel from '../../model/Service.model';
import { useLoading } from '../../utils/LoadingUtil';
import axiosInstance from '../../utils/axiosInstance';
import { ConfirmAlert, showAlert } from '../../components';
import validateForm from '../../utils/validateForm';
import { handleApiError } from '@/lib/handleApiError';

const ServiceContextProvider = ({ children }) => {
  const { service, setService, initialState } = serviceModel();
  const { startLoading, stopLoading } = useLoading();
  const [error, setError] = useState(null);
  const [serviceData, setserviceData] = useState([]);

  const isValidData = useCallback(
    (service) => {
      const errMsg = {
        service: 'Service is required',
      };

      const isValid = validateForm(service, setError, errMsg);
      if (!isValid) {
        stopLoading();
        return false;
      }

      return true;
    },
    [stopLoading],
  );

  const getAllService = useCallback(async () => {
    startLoading();
    try {
      const response = await axiosInstance.get('/SmartProAPI/api/services');
      setserviceData(response.data);
    } catch (error) {
      handleApiError('error', error.response?.data, error?.response?.status);
    }
  });

  const deleteService = useCallback(
    async (id) => {
      const result = await ConfirmAlert({ confirmBtnText: 'Delete Service' });
      if (!result.isConfirmed) {
        showAlert('error', 'Service deletion cancelled');
        return;
      }
      startLoading();
      try {
        await axiosInstance.delete(`/SmartProAPI/api/services/${id}`);
        showAlert('success', 'Service deleted successfully');
        getAllService();
      } catch (error) {
        handleApiError('error', error.message, error?.response?.status);
      } finally {
        stopLoading();
      }
    },
    [getAllService, startLoading, stopLoading],
  );

  const createNewService = useCallback(
    async (service, clickServiceShow) => {
      startLoading;
      if (!isValidData(service)) return;
      try {
        await axiosInstance.post('/SmartProAPI/api/services', service);
        showAlert('success', 'Services added successfully');
        clearFormData();
        clickServiceShow();
        getAllService();
      } catch (error) {
        setError(error);
        handleApiError('error', error.response?.data, error?.response?.status);
      } finally {
        stopLoading();
      }
    },
    [getAllService, isValidData, startLoading, stopLoading],
  );

  const updateService = useCallback(
    async (service, id, clickServiceShow) => {
      startLoading(); // Start loading state
      if (!isValidData(service)) return; // Validate the service data
      try {
        await axiosInstance.put(`/SmartProAPI/api/services/${id}`, service); // Make the API call with PUT or PATCH
        showAlert('success', 'Service updated successfully'); // Show success alert
        clickServiceShow(); // Close the modal or form
        clearFormData(); // Clear the form data
        await getAllService();
      } catch (error) {
        setError(error); // Set the error state
        handleApiError('error', error.response?.data, error?.response?.status); // Show error alert
      } finally {
        stopLoading(); // Stop the loading state
      }
    },
    [getAllService, isValidData, startLoading, stopLoading],
  );

  const clearFormData = useCallback(() => {
    setError(null);
    setService(initialState);
  }, [initialState, setService]);
  return (
    <ServiceContext.Provider
      value={{
        getAllService,
        serviceData,
        setserviceData,
        error,
        setError,
        service,
        setService,
        deleteService,
        createNewService,
        updateService,
        clearFormData,
      }}
    >
      {children}
    </ServiceContext.Provider>
  );
};

export default ServiceContextProvider;
export const useService = () => useContext(ServiceContext);
